import { useState } from 'react';

interface ProductGalleryProps {
  mainImage: string;
  extraImages: string[];
  name: string;
}

export default function ProductGallery({ mainImage, extraImages, name }: ProductGalleryProps) {
  const allImages = [mainImage, ...extraImages];
  const [selected, setSelected] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      {/* Main Image */}
      <div
        className="relative w-full h-[480px] rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 cursor-zoom-in"
        onClick={() => setZoomed(true)}
      >
        <img
          src={allImages[selected]}
          alt={name}
          className="w-full h-full object-cover object-top transition-all duration-500"
        />
        <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-gray-600">
          <i className="ri-zoom-in-line text-sm" />
        </div>
        {/* Nav arrows */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setSelected((s) => (s - 1 + allImages.length) % allImages.length); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-700 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setSelected((s) => (s + 1) % allImages.length); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-700 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </>
        )}
        {/* Dot indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
          {allImages.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setSelected(i); }}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${selected === i ? 'w-5 bg-[#FF6B35]' : 'w-1.5 bg-white/70'}`}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails */}
      <div className="flex gap-3">
        {allImages.map((img, i) => (
          <button
            key={i}
            onClick={() => setSelected(i)}
            className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer ${
              selected === i ? 'border-[#FF6B35] scale-95' : 'border-transparent hover:border-gray-200'
            }`}
          >
            <img src={img} alt={`${name} view ${i + 1}`} className="w-full h-full object-cover object-top" />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {zoomed && (
        <div
          className="fixed inset-0 bg-black/85 z-[200] flex items-center justify-center p-8 cursor-zoom-out"
          onClick={() => setZoomed(false)}
        >
          <button
            onClick={() => setZoomed(false)}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={allImages[selected]}
            alt={name}
            className="max-h-[85vh] max-w-[85vw] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
